/**
 * The receipt contract (#495 "Per-unit tax basis on inventory: the Paid Tax
 * toggle becomes the Reg 1660 election record", #1193 "Receipt parser: one
 * per store, misses go to admin"; docs/plans/taxes.md, The vendor side 2, 6
 * and 7). A vendor uploads a receipt through the signed-url upload, then
 * names it here; the backend reads it and answers what it read and every
 * reason the read is a miss. Once an upload is read, the receipts waiting are
 * judged as one batch: those that need nothing from the vendor are written
 * and ready to sign, and the rest go through up to four screens and are
 * written together at Add to inventory; one signature then approves every
 * settled receipt (#1272 "WEB: receipts reviewed as a batch, signed
 * together"; docs/plans/receipts-redesign.md, Decisions 14 to 25).
 *
 * The source, miss and math-problem kinds live here, not in the backend,
 * because they are on the wire: the backend's reader imports them.
 *
 * Web only: vendors manage inventory on web, so mobile imports none of this.
 */
import { z } from 'zod';
import {
  ApiDateString,
  envelope,
  IsoDateTimeString,
  NonNegativeMoneyCents,
  pageOf,
  ShortLivedLinkEnvelopeSchema,
  UsStateCodeSchema,
} from './_primitives';
import { MAX_RECEIPTS_PER_UPLOAD, type UploadType } from './upload';
import { SerialNumbersSchema } from './inventoryItemFields';

/** The upload folder a receipt goes to; saving a receipt refuses a key from any other folder. */
export const RECEIPT_UPLOAD_TYPE = 'receipts' as const satisfies UploadType;

export const RECEIPT_ROUTES = {
  /** GET: a page of the vendor's receipts (ListReceiptsQuerySchema), each with its latest signature. */
  receipts: 'vendor/receipts',
  /**
   * POST (UploadReceiptsRequestSchema): every file of one drop, which the
   * backend reads in the background a file at a time; answers the upload as
   * it stands (ReceiptUploadSchema), nothing read yet.
   * GET: the uploads Tax Compliance's banners show (ReceiptUploadListSchema).
   */
  uploads: 'vendor/receipts/uploads',
  /** POST, no body: dismiss the banner listing a finished upload's refused files (Decision 58). */
  uploadDismiss: (id: number | string) => `vendor/receipts/uploads/${id}/dismiss`,
  /**
   * GET: one of the vendor's receipts, with its latest signature.
   * DELETE: delete it, signed too, with everything pointing at it; its gear stays (Decision 81).
   */
  receipt: (id: number | string) => `vendor/receipts/${id}`,
  /**
   * GET: the batch waiting on the vendor, what each of its screens shows (BatchSchema), with the
   * receipts the vendor set aside to wait for a fix left out (BatchQuerySchema).
   * POST: Add to inventory (AddToInventoryRequestSchema): writes the whole batch at once.
   */
  batch: 'vendor/receipts/batch',
  /**
   * POST, no body: write every receipt of the batch that needs nothing from the vendor, judged
   * against the whole batch (Decision 22); answers whether any still needs the screens (BatchSettledSchema).
   */
  batchSettle: 'vendor/receipts/batch/settle',
  /**
   * POST, no body: tell support something on the receipt was read wrong; the
   * backend writes it into the vendor's support thread and answers the
   * thread to open (SupportThreadSchema, support.ts). A vendor never edits
   * what a receipt says (Decision 13).
   */
  misread: (id: number | string) => `vendor/receipts/${id}/misread`,
  /** GET: the settled receipts not yet signed, with the gear each points at (ReadyReceiptsSchema). */
  ready: 'vendor/receipts/ready',
  /** POST: sign the settled receipts the body names (SignReceiptsRequestSchema). */
  signature: 'vendor/receipts/signature',
  /** GET: one of the signed-in vendor's own receipt files, inline and uncached, in one request. */
  file: (id: number | string) => `vendor/receipts/${id}/file`,
  /** GET: every item in the vendor's inventory with the units its signed receipts show the tax paid on. */
  taxPaid: 'vendor/receipts/tax-paid',
} as const;

/**
 * Admin's view of a receipt: where a miss alert links (#1193). The routes
 * are the backend's /admin/receipts mount.
 */
export const ADMIN_RECEIPT_ROUTES = {
  /** GET: the receipt, its vendor and uploader, and what was read. */
  receipt: (id: number | string) => `admin/receipts/${id}`,
  /** GET: a five-minute link that opens the receipt's file in a new tab. */
  fileUrl: (id: number | string) => `admin/receipts/${id}/file-url`,
  /** POST, no body: read the stored file again with today's parsers; answers the receipt as re-read. Unsigned only. */
  readAgain: (id: number | string) => `admin/receipts/${id}/read-again`,
  /** GET: a five-minute link that opens the proof of payment the vendor attached (Decision 37). */
  paymentProofUrl: (id: number | string) => `admin/receipts/${id}/payment-proof-url`,
  /**
   * GET: a five-minute link that downloads, as one zip, every receipt of an
   * upload its miss alert names, so their layouts can be built into the
   * parsers at once (Tyler, 2026-10-16: "I have 88 misses i want to download
   * all misses on a notification").
   */
  uploadMissesUrl: (uploadId: number | string) => `admin/receipts/uploads/${uploadId}/misses-url`,
  /** GET ?token=: the zip itself, which that link opens. */
  uploadMisses: (uploadId: number | string) => `admin/receipts/uploads/${uploadId}/misses`,
  /**
   * POST, no body: read again with today's readers every receipt the
   * upload's miss alert still names, queued a few at a time; the alert
   * closes once none is missed (Tyler, 2026-10-16: "the bulk read again we
   * need"). Answers how many were queued.
   */
  uploadReadAgain: (uploadId: number | string) => `admin/receipts/uploads/${uploadId}/read-again`,
  /**
   * POST, no body: read again with today's readers every receipt the store
   * has not signed, from the Users page's Force actions (Tyler, 2026-10-19:
   * "lets give the admin a way to re-read a users whole receipts"). Behind
   * the re-enter prompt: it clears the vendor's reviews of them (Decision
   * 123). Answers how many were queued.
   */
  storeReadAgain: (vendorId: string) => `admin/receipts/stores/${vendorId}/read-again`,
} as const;

/** POST an upload's or a store's read-again: how many of its receipts were queued to be read again. */
export const ReadAgainQueuedSchema = z.object({ queued: z.number().int().positive() });
export type ReadAgainQueued = z.infer<typeof ReadAgainQueuedSchema>;
export const ReadAgainQueuedEnvelopeSchema = envelope(ReadAgainQueuedSchema);
export const unwrapReadAgainQueued = (response: z.infer<typeof ReadAgainQueuedEnvelopeSchema>) => response.data;

/**
 * The admin alert an upload's receipt misses send (#1193), as the backend's
 * ALERT_TYPES names it and admin's notifications feed finds it.
 */
export const RECEIPT_MISS_ALERT_TYPE = 'RECEIPT_MISS';

/**
 * A receipt miss alert's details: the upload, which its download is asked
 * by, and the lists the feed shows. An alert sent before uploads were read
 * in the background names one receipt and no upload, so it fails this and
 * has nothing to download.
 */
export const ReceiptMissAlertDetailsSchema = z.object({
  uploadId: z.number().int().positive(),
  /** The receipt ids, comma separated. */
  receipts: z.string(),
  /** The miss kinds across them, comma separated. */
  misses: z.string(),
  /** The stores whose parser read them, or that none had one yet. */
  stores: z.string(),
  /** The file names as uploaded, comma separated. */
  files: z.string(),
  /** While admin's Read again reads the upload's receipts: how many of its files are read, as the vendor's banner says it (uploadReadCountText). */
  readAgain: z.string().optional(),
});
export type ReceiptMissAlertDetails = z.infer<typeof ReceiptMissAlertDetailsSchema>;

/**
 * The stores with a receipt parser of their own, by the key a read keeps
 * (a receipt's storeKey), and the name a vendor and admin see. The
 * backend's receiptStores holds the parsers and names each from here, and
 * the Taxes page's store filter lists them, so a store has one name.
 */
export const RECEIPT_STORE_KEYS = ['bh', 'adorama', 'filmtools', 'bestbuy', 'amazon', 'tilta', 'bandpro'] as const;
export type ReceiptStoreKey = (typeof RECEIPT_STORE_KEYS)[number];
export const RECEIPT_STORE_NAMES: Record<ReceiptStoreKey, string> = {
  bh: 'B&H Photo',
  adorama: 'Adorama',
  filmtools: 'Filmtools',
  bestbuy: 'Best Buy',
  amazon: 'Amazon',
  tilta: 'Tilta',
  bandpro: 'Band Pro',
};
/** Whether a receipt's storeKey is a store with a parser, so it has a name here. */
const isReceiptStoreKey = (key: string): key is ReceiptStoreKey =>
  (RECEIPT_STORE_KEYS as readonly string[]).includes(key);

/** The name of the store whose parser read a receipt; null for one no store's parser read. */
export const receiptStoreNameOf = (storeKey: string | null): string | null =>
  storeKey !== null && isReceiptStoreKey(storeKey) ? RECEIPT_STORE_NAMES[storeKey] : null;

/** Where a read's text came from: a PDF's own text never leaves our server; a scan or a photo goes to Google Vision. */
export const RECEIPT_SOURCE = {
  PDF_TEXT: 'PDF_TEXT',
  SCANNED_PDF: 'SCANNED_PDF',
  PHOTO: 'PHOTO',
} as const;

/** Every reason a read is a miss. */
export const RECEIPT_MISS = {
  /** The file has no text to read. */
  NO_TEXT: 'NO_TEXT',
  /** A scan longer than Google Vision reads at once. */
  TOO_MANY_PAGES: 'TOO_MANY_PAGES',
  /** Reading failed outright (Google unreachable, a corrupt file); the vendor fills it in by hand. */
  READ_FAILED: 'READ_FAILED',
  /** A store with no parser of its own yet. */
  UNKNOWN_STORE: 'UNKNOWN_STORE',
  /** Priced lines the parser could not understand, named in `lines`. */
  UNREAD_LINES: 'UNREAD_LINES',
  /** Amounts that do not add up, named in `problems`. */
  MATH: 'MATH',
  /** The receipt shows no tax (Decision 24): the record needs it, so admin and the vendor see it; signing still goes ahead. */
  NO_TAX: 'NO_TAX',
  /**
   * The receipt shows the money did not go through or came back (Decisions
   * 24 and 122): why in `reason`, the store's own word in `status` where it
   * prints one, and the lines that show it in `lines` (Decision 91).
   */
  NO_PAYMENT: 'NO_PAYMENT',
  /**
   * The tax the receipt charged is at no California rate (Decision 31): below
   * the statewide 7.25% or above the highest rate in CDTFA's current table.
   * Named in `rateBasisPoints`. The vendor's warning only, never an admin
   * alert (Tyler, 2026-09-29); signing goes ahead.
   */
  NOT_CALIFORNIA_RATE: 'NOT_CALIFORNIA_RATE',
} as const;

/**
 * Why a receipt shows no payment, from what its store's reader saw
 * (docs/plans/receipts-redesign.md, Decision 91; Tyler, 2026-10-19: "it
 * should highlight the piece that is causing the issue").
 */
export const NO_PAYMENT_REASON = {
  /** The store marks the order refunded: Amazon's refund of every item, Magento's Closed at Filmtools. */
  REFUNDED: 'REFUNDED',
  /** The store marks the order canceled: never charged. */
  CANCELED: 'CANCELED',
  /** The store marks the order on hold: not yet paid. */
  ON_HOLD: 'ON_HOLD',
  /** The store marks the order with another status than its complete one, named in `status`. */
  NOT_COMPLETE: 'NOT_COMPLETE',
  /** The payment lines it prints pay nothing ("CASH .00" on a no-charge replacement). */
  PAID_NOTHING: 'PAID_NOTHING',
  /** Its total is $0.00, at any store. */
  NOTHING_CHARGED: 'NOTHING_CHARGED',
} as const;

/** The ways a read's amounts can disagree. */
export const RECEIPT_MATH_PROBLEM = {
  NO_LINES: 'NO_LINES',
  LINE_TOTAL: 'LINE_TOTAL',
  LINES_VS_SUBTOTAL: 'LINES_VS_SUBTOTAL',
  MISSING_TOTAL: 'MISSING_TOTAL',
  TOTAL_MISMATCH: 'TOTAL_MISMATCH',
} as const;

/** Each way the amounts can disagree, in words (mathProblemText). */
const RECEIPT_MATH_PROBLEM_LABELS: Record<keyof typeof RECEIPT_MATH_PROBLEM, string> = {
  [RECEIPT_MATH_PROBLEM.NO_LINES]: 'No item lines were read',
  [RECEIPT_MATH_PROBLEM.LINE_TOTAL]: 'Quantity times price is not the line total',
  [RECEIPT_MATH_PROBLEM.LINES_VS_SUBTOTAL]: 'The lines do not add up to the subtotal',
  [RECEIPT_MATH_PROBLEM.MISSING_TOTAL]: 'No total was read',
  [RECEIPT_MATH_PROBLEM.TOTAL_MISMATCH]: 'Subtotal, discount, shipping, fees and tax do not make the total',
};

export const UploadReceiptRequestSchema = z.object({
  /** The key the signed-url upload wrote: the caller's own upload, or the save is refused. */
  s3Key: z.string().min(1),
  originalFilename: z.string().trim().min(1).max(255),
});
export type UploadReceiptRequest = z.infer<typeof UploadReceiptRequestSchema>;

/**
 * POST vendor/receipts/uploads: every file of one drop, read in the
 * background a file at a time (docs/plans/receipts-redesign.md, Reading
 * uploads in the background 2). Add receipts is the one way to upload
 * receipts.
 */
export const UploadReceiptsRequestSchema = z.object({
  files: z.array(UploadReceiptRequestSchema).min(1).max(MAX_RECEIPTS_PER_UPLOAD),
});
export type UploadReceiptsRequest = z.infer<typeof UploadReceiptsRequestSchema>;

/** A file of an upload that was not stored, and why. `position` is its place in the drop, which keeps two files of one name apart. */
export const RefusedReceiptSchema = z.object({
  position: z.number().int().nonnegative(),
  fileName: z.string(),
  reason: z.string(),
});
export type RefusedReceipt = z.infer<typeof RefusedReceiptSchema>;

/**
 * An upload as it is read: how many files it has, how many are stored as
 * receipts and not waiting to be read again, each refused one, and whether
 * its last file is read and the batch settled (Decision 22). The files still
 * waiting are the rest. `readingAgain` is true while admin's Read again reads
 * some of its receipts again (receipts-redesign.md, Reading uploads in the
 * background 18): the upload is not finished again until they are read.
 */
export const ReceiptUploadSchema = z.object({
  id: z.number().int(),
  files: z.number().int().positive(),
  stored: z.number().int().nonnegative(),
  refused: z.array(RefusedReceiptSchema),
  finished: z.boolean(),
  readingAgain: z.boolean(),
});
export type ReceiptUpload = z.infer<typeof ReceiptUploadSchema>;

/** An upload's files read so far: stored or refused. One waiting to be read again is not, as `stored` leaves it out. */
export const uploadFilesRead = (upload: ReceiptUpload) => upload.stored + upload.refused.length;

/**
 * How many of an upload's files are read, in the banner's words (Tyler,
 * 2026-10-13: Draft 1), which admin's miss alert shows too while its
 * receipts are read again.
 */
export const uploadReadCountText = (upload: ReceiptUpload) => `${uploadFilesRead(upload)} of ${upload.files} read`;
export const ReceiptUploadEnvelopeSchema = envelope(ReceiptUploadSchema);
export const unwrapReceiptUpload = (response: z.infer<typeof ReceiptUploadEnvelopeSchema>) => response.data;

/**
 * The socket events of receipt uploads. The read job tells the uploader's
 * own socket each time a file of their upload is read and when it finishes,
 * never on a timer (Tyler, 2026-10-13: "We don't poll here thats why we use
 * sockets"). The push carries the upload as the list answers it, so the page
 * shows it without reading the list again (docs/plans/receipts-redesign.md,
 * Requests the receipt pages send 6).
 */
export const RECEIPT_SOCKET_EVENTS = {
  UPLOAD_CHANGED: 'receipt:upload:changed',
  /**
   * A store's receipts changed outside its own pages (support read one
   * again): the pages keep what they read until something marks it stale,
   * so this marks it. Carries nothing; the pages read again.
   */
  RECEIPTS_CHANGED: 'receipt:receipts:changed',
} as const;
/** What RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED carries: the upload as it stands now. */
export const ReceiptUploadChangedSchema = z.object({ upload: ReceiptUploadSchema });
export type ReceiptUploadChanged = z.infer<typeof ReceiptUploadChangedSchema>;

/**
 * GET vendor/receipts/uploads: what Tax Compliance's banners show, oldest
 * first. Each upload still being read (Decision 56), and each finished with
 * refused files the vendor has not dismissed (Decision 58); never a
 * replacement the batch review's Upload a receipt made before it went
 * (Decisions 60 and 74).
 */
export const ReceiptUploadListSchema = z.array(ReceiptUploadSchema);
export const ReceiptUploadListEnvelopeSchema = envelope(ReceiptUploadListSchema);
export const unwrapReceiptUploadList = (response: z.infer<typeof ReceiptUploadListEnvelopeSchema>) => response.data;

/**
 * The reader keeps everything a receipt shows (Decision 109; Tyler,
 * 2026-10-07: "these should all be kept"). A store's own reader knows its
 * layout, so it always says: an empty list is a receipt that prints none.
 * Absent means the read did not look: a receipt read before 2026-10-07,
 * which admin's Read again (Decision 40) fills in, or one the general
 * reader read, which knows no store's layout.
 */

/** One unit's serial number per value, as the store prints it ("SERIAL #: CL0754572 CL0754580" is two). */

/**
 * One part of a kit, as the receipt lists it under the kit's line (B&H's
 * "CONSISTS OF:"). A part has no price of its own: the kit's line carries
 * it, so the parts never change what the lines add up to.
 */
export const ReceiptLinePartSchema = z.object({
  description: z.string(),
  /** Units of the part in the kit's line, as printed. */
  quantity: z.number().int().positive(),
  /** The store's own SKU for the part, when printed. */
  storeSku: z.string().min(1).optional(),
  /** The maker's part number, when printed under the part. */
  partNumber: z.string().min(1).optional(),
  serialNumbers: SerialNumbersSchema,
});
export type ReceiptLinePart = z.infer<typeof ReceiptLinePartSchema>;

export const ReceiptLineSchema = z.object({
  description: z.string(),
  quantity: z.number().int().positive(),
  /** The printed price of one unit: what the gear costs to replace, before any credit under the line. */
  unitPriceCents: NonNegativeMoneyCents,
  lineTotalCents: NonNegativeMoneyCents,
  /**
   * A credit the store printed under the line (B&H's "Free item when
   * purchased … -49.95"), kept apart from the printed price so a free item
   * still costs its price to replace (#1271 "Receipt lines become gear
   * rows"). What the lines add up to nets it. Absent when none.
   */
  creditCents: NonNegativeMoneyCents.optional(),
  /**
   * The maker's part number, when the receipt prints one: B&H in its
   * salesperson code's brackets, Adorama on its "Mfg Item#:" line, Best Buy
   * at the end of the description; Filmtools prints none. It matches the
   * line to the catalog for certain (Decision 32).
   */
  partNumber: z.string().min(1).optional(),
  /**
   * The store's own SKU for the line, as printed: B&H's "RO5015TC", a used
   * item's "USED: 3539778" (Decision 109). Absent when the receipt prints
   * none, as a Filmtools gift card, or when the read did not look.
   */
  storeSku: z.string().min(1).optional(),
  /** The serial numbers printed under the line (Decision 109). */
  serialNumbers: SerialNumbersSchema.optional(),
  /** A kit's parts, in the receipt's order (Decision 109); empty for any other line. */
  parts: z.array(ReceiptLinePartSchema).optional(),
  /**
   * The options printed under the line, which say which variant was bought:
   * Filmtools' "Color: Black", "Circular Filter Size: 77mm" (Decision 109).
   */
  options: z.array(z.object({ name: z.string().min(1), value: z.string() })).optional(),
});
export type ReceiptLine = z.infer<typeof ReceiptLineSchema>;

export const ReceiptReadSchema = z.object({
  lines: z.array(ReceiptLineSchema),
  unreadLines: z.array(z.string()),
  subtotalCents: NonNegativeMoneyCents.nullable(),
  discountCents: NonNegativeMoneyCents.nullable(),
  shippingCents: NonNegativeMoneyCents.nullable(),
  /** Fees that are neither an item nor tax, like California's e-waste fee; part of the total. */
  feesCents: NonNegativeMoneyCents.nullable(),
  taxCents: NonNegativeMoneyCents.nullable(),
  totalCents: NonNegativeMoneyCents.nullable(),
  orderNumber: z.string().nullable(),
  /**
   * The day the order was placed, as the receipt prints it (Decision 109).
   * Null when the receipt shows none; absent when the read did not look.
   */
  orderDate: ApiDateString.nullable().optional(),
  /**
   * The state the gear was bought in, as the receipt shows it (Decision 30):
   * where it shipped, or the store's own for a pickup or an in-store sale.
   * Null when the receipt does not show it, so the vendor says.
   */
  boughtInState: UsStateCodeSchema.nullable(),
});
export type ReceiptRead = z.infer<typeof ReceiptReadSchema>;

export const ReceiptMathProblemSchema = z.object({
  kind: z.enum(RECEIPT_MATH_PROBLEM),
  /** The line whose own math is off, for LINE_TOTAL. */
  lineIndex: z.number().int().nonnegative().optional(),
});

/** Hundredths of a percent in a whole percent. */
const BASIS_POINTS_PER_PERCENT = 100;

/** A miss's rateBasisPoints as the percent a receipt prints (1025 is "10.25%"), one wording for both apps. */
export const ratePercentText = (basisPoints: number) => `${(basisPoints / BASIS_POINTS_PER_PERCENT).toFixed(2)}%`;

/** A store's status word that already says refunded ("Refunded"), so the sentence need not explain it. */
const SAYS_REFUNDED = /refund/i;

/**
 * Why a receipt shows no payment, in one sentence for the vendor's Payment
 * section and admin's receipt page alike (Decision 91). Drafts: "make them
 * then we will work on the copy". A miss saved before reasons were kept says
 * what every one said then, until its receipt is read again.
 */
export const noPaymentText = (miss: { reason?: keyof typeof NO_PAYMENT_REASON; status?: string }, storeName: string | null): string => {
  const store = storeName === null ? 'The store' : storeName;
  switch (miss.reason) {
    case undefined:
      return 'This receipt does not show that it was paid.';
    case NO_PAYMENT_REASON.REFUNDED:
      return miss.status === undefined || SAYS_REFUNDED.test(miss.status)
        ? 'This order was refunded.'
        : `${store} marks this order ${miss.status}, which means it was refunded.`;
    case NO_PAYMENT_REASON.CANCELED:
      return 'This order was canceled.';
    case NO_PAYMENT_REASON.ON_HOLD:
      return 'This order is on hold and not yet paid.';
    case NO_PAYMENT_REASON.NOT_COMPLETE:
      if (miss.status === undefined) throw new Error('A NOT_COMPLETE miss names the status the store printed');
      return `${store} marks this order ${miss.status}, not complete.`;
    case NO_PAYMENT_REASON.PAID_NOTHING:
      return 'The payment it prints paid nothing.';
    case NO_PAYMENT_REASON.NOTHING_CHARGED:
      return 'This receipt charged nothing.';
  }
};

export const ReceiptMissSchema = z
  .object({
    kind: z.enum(RECEIPT_MISS),
    /** The lines it names: those not understood, for UNREAD_LINES; those showing no payment, for NO_PAYMENT. */
    lines: z.array(z.string()).optional(),
    problems: z.array(ReceiptMathProblemSchema).optional(),
    /** The rate the receipt charged, in hundredths of a percent (1025 is 10.25%), for NOT_CALIFORNIA_RATE. */
    rateBasisPoints: z.number().int().nonnegative().optional(),
    /** Why the receipt shows no payment, for NO_PAYMENT; none on one saved before Decision 91, which says so until it is read again. */
    reason: z.enum(NO_PAYMENT_REASON).optional(),
    /** The order status the store printed, for NO_PAYMENT when its store prints one ("Closed"). */
    status: z.string().optional(),
  })
  .refine((miss) => miss.kind !== RECEIPT_MISS.NOT_CALIFORNIA_RATE || miss.rateBasisPoints !== undefined, {
    message: 'A NOT_CALIFORNIA_RATE miss names the rate the receipt charged',
    path: ['rateBasisPoints'],
  });

/**
 * One way a receipt's amounts disagree, in words, with the line it names
 * (LINE_TOTAL names one): admin's receipt page and the vendor's review say
 * it alike. The server's math check makes the index from the read's own
 * lines, so a line the read does not have is a corrupt row: it throws
 * rather than show a problem with no line.
 */
export const mathProblemText = (
  receiptId: number,
  read: ReceiptRead | null,
  problem: z.infer<typeof ReceiptMathProblemSchema>,
): string => {
  const label = RECEIPT_MATH_PROBLEM_LABELS[problem.kind];
  if (problem.lineIndex === undefined) return label;
  const line = read === null ? undefined : read.lines[problem.lineIndex];
  if (line === undefined) {
    throw new Error(`Receipt ${receiptId}: a math problem names line ${problem.lineIndex}, which its read does not have`);
  }
  return `${label}: ${line.description}`;
};

export const ReceiptSchema = z.object({
  id: z.number().int(),
  originalFilename: z.string(),
  /** Null when reading failed before any text was produced. */
  source: z.enum(RECEIPT_SOURCE).nullable(),
  /** The store whose parser read it; null for a store with no parser yet. */
  storeKey: z.string().nullable(),
  orderNumber: z.string().nullable(),
  /** Null when there was nothing to read. */
  read: ReceiptReadSchema.nullable(),
  /** Empty when the read can be trusted. */
  misses: z.array(ReceiptMissSchema),
  /** This vendor's earlier receipts from the same store with the same order number: likely the same order twice. */
  sameOrderReceiptIds: z.array(z.number().int()),
  createdAt: IsoDateTimeString,
});
export type Receipt = z.infer<typeof ReceiptSchema>;

/**
 * Proof of payment a vendor attached to a receipt that shows none, such as
 * a card statement, while the review offered it (docs/plans/
 * receipts-redesign.md, Decision 37). None is attached since Decision 78;
 * admin's receipt still shows one kept before.
 */
export const PaymentProofSchema = z.object({
  originalFilename: z.string(),
  /** The type read from the file's own bytes. */
  mimeType: z.string(),
  createdAt: IsoDateTimeString,
});
export type PaymentProof = z.infer<typeof PaymentProofSchema>;

// ─── Signing a receipt (docs/plans/taxes.md, The vendor side 2) ─────────

/**
 * The words a vendor signs for a receipt, the owner's statement Reg
 * 1686(b)(2) describes, in its own test: "leased in substantially the same
 * form as acquired" (CDTFA advice F26-08-011; Tyler, 2026-10-03). The backend
 * stores the words with each signature, so changing them never rewrites what
 * an earlier signature agreed to. Signing says none of the items was
 * rehoused, rebuilt or assembled from parts: there is no exception to mark
 * (docs/plans/receipts-redesign.md, Decision 24; Tyler, 2026-10-09).
 */
export const RECEIPT_STATEMENT =
  'I bought the items listed here as these receipt shows, and my answers about them are true. ' +
  'I rent them in substantially the same form I bought them in: none has been rehoused, rebuilt or assembled from parts.';

/** How a receipt's gear row found gear the vendor owns (the backend's receiptProposal). */
export const RECEIPT_ROW_MATCH = {
  /** The line's maker's part number named a catalog model, and the vendor owns gear of that model. */
  CATALOG_MODEL: 'CATALOG_MODEL',
  /** Gear the vendor owns has the very name the new gear would take, so the line links it rather than make a second. */
  SAME_NAME: 'SAME_NAME',
  /**
   * The catalog's text search put a model first for the line's words, and the
   * vendor owns gear of that model (docs/plans/receipts-redesign.md, Decision
   * 1's second flow). No longer made: since Decision 102 only a catalog model
   * or an exact name links unasked, and the search's model is Name your gear's
   * guess (Decision 105), which the vendor sees (NAMED_MODEL). Kept for the
   * rows settled with it.
   */
  SUGGESTED_MODEL: 'SUGGESTED_MODEL',
  /**
   * The line's words named gear the vendor owns (docs/plans/receipts-redesign.md,
   * Decision 101). No longer made: since Decision 102 the words only offer
   * gear, which the vendor picks (PICKED). Kept for the rows settled with it.
   */
  SAME_WORDS: 'SAME_WORDS',
  /**
   * The vendor named the line their item on the review's Name your gear: one
   * its words found, or one they found with I already have this (Decisions
   * 102 and 112). Rows settled before Decision 112 were picked on New gear.
   */
  PICKED: 'PICKED',
  /**
   * The line's model was named, on the review's Name your gear or by the
   * vendor's earlier receipts printing the line the same way (Decisions 104
   * and 106), and the vendor owns gear of that model.
   */
  NAMED_MODEL: 'NAMED_MODEL',
} as const;

/** What every row of a signed receipt says about its gear. */
const receiptRowFields = {
  /**
   * The part of a kit line this row is for, by its place under the line
   * (ReceiptLineSchema.parts), or null for the line itself. A store's kit is a
   * bundle, not a GearGhost kit (docs/plans/receipts-redesign.md, Decision 2):
   * each part is its own gear, so each is its own row.
   */
  partIndex: z.number().int().nonnegative().nullable().optional(),
  /** How many of it this receipt bought. */
  units: z.number().int().positive(),
  /** Rehoused, rebuilt or assembled from parts: a modified unit does not count as tax paid. */
  modified: z.boolean(),
};

/** Where a row's gear is on the receipt: its line, and its part under the line when it is one. */
export const rowPlaceOf = (row: { lineIndex: number; partIndex: number | null }): string =>
  `${row.lineIndex}:${row.partIndex === null ? '' : row.partIndex}`;

// ─── The batch review (#1272 "WEB: receipts reviewed as a batch, signed together") ───
//
// docs/plans/receipts-redesign.md, Decisions 14 to 25. The upload only reads.
// Once an upload is read, the receipts waiting are judged as one batch: a
// receipt that needs nothing from the vendor is written and ready to sign;
// the rest go through up to four screens (Read trouble, California sales tax,
// How many you own, New inventory) and are written together at Add to
// inventory. Signing approves what the settled receipts point at and changes
// no gear.

/** A place on the receipt: its line, and the part under the line when the row is one of a kit's parts (Decision 2). */
const ReceiptPlaceSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  partIndex: z.number().int().nonnegative().nullable(),
});

/** Each place on the receipt once: a line, or a part under it, is one row. */
const eachPlaceOnce = (rows: Array<{ lineIndex: number; partIndex: number | null }>) =>
  new Set(rows.map(rowPlaceOf)).size === rows.length;

/**
 * One row of a settled receipt, kept on it (the backend's `receipt.gear`):
 * the inventory item the row is, made at Add to inventory (`made`) or the
 * vendor's own, linked by `match`; how many units its line bought; and
 * whether they were modified. What signing turns into the signature's items.
 * A line the vendor left out (Don't add, Decision 23) has no row.
 */
export const SettledRowSchema = ReceiptPlaceSchema.extend({
  inventoryItemId: z.number().int().positive(),
  /** True for an item the batch made from the line; false for gear the vendor owned already. */
  made: z.boolean(),
  /** How the row found the vendor's gear; null for a row that made its item. */
  match: z.enum(RECEIPT_ROW_MATCH).nullable(),
  /**
   * How many units the line bought. How many of them the receipt covers is
   * worked out when it is read, the units the vendor owns going to the
   * newest receipts first (Decision 27), so it is never kept here.
   */
  units: z.number().int().positive(),
  /** Rehoused, rebuilt or assembled from parts: a modified unit does not count as tax paid. */
  modified: z.boolean(),
});
export type SettledRow = z.infer<typeof SettledRowSchema>;

/**
 * A settled receipt's review, as the receipt keeps it: its rows, and the
 * answers signing will sign, the read's where it could tell and the vendor's
 * where it could not. A receipt not settled keeps none (null). Its rows can
 * be none, when every line was left out (Decision 25); the state is the
 * read's, null when the receipt shows none, since it is never asked
 * (Decision 19).
 */
export const SettledReviewSchema = z.object({
  rows: z
    .array(SettledRowSchema)
    .refine((rows) => new Set(rows.map((row) => row.inventoryItemId)).size === rows.length, 'Each item goes on a receipt once.')
    .refine(eachPlaceOnce, 'Each line of the receipt is one row.'),
  purchaseState: UsStateCodeSchema.nullable(),
  paidCaliforniaSalesTax: z.boolean(),
});
export type SettledReview = z.infer<typeof SettledReviewSchema>;

/** What the batch's screens and the sign screen name a receipt by. */
const receiptHeadingFields = {
  id: z.number().int(),
  originalFilename: z.string(),
  /** The name of the store whose parser read it; null for a store with no parser yet. */
  storeName: z.string().nullable(),
  orderNumber: z.string().nullable(),
  /** The day the order was placed, as the receipt prints it; null when it prints none. */
  orderDate: ApiDateString.nullable(),
};

/**
 * Where a file of a receipt stands, on the receipt's card (Decision 84):
 * signed; settled and waiting for its signature; or neither, a copy or a
 * receipt waiting for its review, which the card names by the day it was
 * uploaded.
 */
export const RECEIPT_FILE_STANDING = {
  SIGNED: 'SIGNED',
  READY_TO_SIGN: 'READY_TO_SIGN',
  UPLOADED: 'UPLOADED',
} as const;

/**
 * What we read wrong on a file, its What we read section (Decisions 17 and
 * 32): the misses that ask whether its gear and proof of purchase were read
 * correctly (amounts that do not add up, priced lines not read, a store we
 * have no reader for yet); its read, which names the lines a math problem is
 * about (mathProblemText) and the tax it shows; the words the receipt
 * printed for the gear we read and for that trouble, each once, which the
 * file beside the card marks (Decisions 30 and 32); and the gear we read,
 * each piece once with the units its lines bought, what is not gear left out.
 */
const BatchTroubleSchema = z.object({
  misses: z.array(ReceiptMissSchema).min(1),
  read: ReceiptReadSchema,
  printed: z.array(z.string()),
  gear: z.array(z.object({ title: z.string(), units: z.number().int().positive() })),
});

/**
 * One file of a receipt on its Check receipts card, with what the card asks
 * of it (Decision 87): What we read when it was read with trouble; Payment
 * when it shows none (Use it or Remove it, Decisions 73 to 75; a file
 * showing no payment is never asked What we read); Sales tax when the read
 * could not tell California sales tax, the misses saying why (Decision 19).
 * A file past the review (signed, or ready to sign) asks nothing.
 */
const BatchCheckFileSchema = z.object({
  id: z.number().int().positive(),
  originalFilename: z.string(),
  standing: z.enum(RECEIPT_FILE_STANDING),
  uploadedAt: IsoDateTimeString,
  trouble: BatchTroubleSchema.nullable(),
  /**
   * The file's NO_PAYMENT miss when Payment asks of it, its reason and the
   * lines showing it for the file beside the card to mark (Decision 91);
   * null when it shows payment, or proof of payment was attached (Decision 37).
   */
  noPayment: ReceiptMissSchema.nullable(),
  /** Empty when the read told the tax. */
  taxMisses: z.array(ReceiptMissSchema),
});

/**
 * A receipt Check receipts asks about, a card of its own (Decisions 86 and
 * 87; Tyler, 2026-10-19: "each receipt here should get a card"): the
 * receipt's heading, then its files. One file for most; more for a receipt
 * the vendor uploaded again, its own file first and each copy after it,
 * oldest first, the vendor keeping one (Decision 84) and the card asking of
 * the file kept. Unanswered, a receipt with copies waits whole, in no batch.
 */
const BatchCheckSchema = z.object({ ...receiptHeadingFields, files: z.array(BatchCheckFileSchema).min(1) });

/**
 * One receipt's units of an item, as How many you own and New inventory list
 * them, newest order first, and the words the receipt printed for the item
 * (the line's, its model number's, its serial numbers'), which the receipt
 * shown beside the screen marks when the item is picked (Tyler, 2026-10-09).
 */
const BatchReceiptUnitsSchema = z.object({ ...receiptHeadingFields, units: z.number().int().positive(), printed: z.array(z.string()) });

/**
 * The catalog brand and model a piece matched, for a card's Brand · Model
 * line (Tyler, 2026-10-19: "when we match i want to see brand and model for
 * the match", "not just the model number"): the brand's name, none when the
 * model has no brand, and the model's name, the catalog's title less its
 * brand.
 */
const GearMatchSchema = z.object({ brand: z.string().nullable(), model: z.string() });
export type GearMatch = z.infer<typeof GearMatchSchema>;

/**
 * An item of the vendor's that the batch's receipts bought more of than it
 * has with no receipt (How many you own, Decisions 14 and 16): its stock on
 * record, every receipt of the batch that bought it, and where the answer
 * starts. An item that keeps no count is never asked.
 */
const BatchOwnedItemSchema = z.object({
  inventoryItemId: z.number().int().positive(),
  title: z.string(),
  /** The item's catalog brand and model; null for an item with no model. */
  match: GearMatchSchema.nullable(),
  /** The item's stock on record. */
  stock: z.number().int().nonnegative(),
  receipts: z.array(BatchReceiptUnitsSchema).min(1),
  /** Every unit the batch's receipts bought of it. */
  units: z.number().int().positive(),
  /**
   * Every unit the vendor's receipts bought of it: the batch's and the
   * earlier receipts', what the card says against the stock, so it adds up
   * (Tyler, 2026-10-19: "otherwise they don't make any sense").
   */
  bought: z.number().int().positive(),
  /**
   * The receipts already added or signed that hold units of it, newest
   * first: what a receipt of the batch's units come after (Decision 27),
   * each with where it stands.
   */
  earlier: z.array(
    BatchReceiptUnitsSchema.extend({ standing: z.enum([RECEIPT_FILE_STANDING.SIGNED, RECEIPT_FILE_STANDING.READY_TO_SIGN]) }),
  ),
  /**
   * Where the answer starts: the inventory's count, which the vendor knows
   * better than their receipts do (Tyler, 2026-10-19: "this screen should be
   * prefilled with the inventory's count not the receipt count"); none for
   * an item they had sold every one of.
   */
  startsAt: z.number().int().nonnegative(),
});

/**
 * Gear the batch would make (New gear, Decisions 14, 20 and 23): one item
 * per catalog model, or per name where no model names it, however many of
 * the batch's receipts bought it. Only gear to make: what a line may be of
 * the vendor's own gear is Name your gear's (Decision 112).
 */
const BatchNewItemSchema = z.object({
  /** Names the item across the batch's receipts, for Add to inventory to answer by. */
  key: z.string(),
  title: z.string(),
  /** The catalog brand and model it is made from; null for gear named from the line's words, admin to give it a model. */
  match: GearMatchSchema.nullable(),
  /** The maker's part number the newest receipt printed for it, kept on the item; null when none printed one. */
  partNumber: z.string().nullable(),
  receipts: z.array(BatchReceiptUnitsSchema).min(1),
  /** Every unit the batch's receipts bought: where How many do you own starts. */
  units: z.number().int().positive(),
});

/** A catalog model as a Name your gear card shows it: its title, and its brand and model for the Brand · Model line (Decision 105). */
const BatchModelChoiceSchema = z.object({
  productModelId: z.number().int().positive(),
  title: z.string(),
  brand: z.string().nullable(),
  model: z.string(),
});

/** An item of the vendor's a Name your gear card offers, with its catalog brand and model; none for an item with no model. */
const BatchYourItemSchema = z.object({ inventoryItemId: z.number().int().positive(), title: z.string(), match: GearMatchSchema.nullable() });

const NamedModelSchema = z.object({ productModelId: z.number().int().positive() });
const NotInCatalogSchema = z.object({ notInCatalog: z.literal(true) });
const YourItemSchema = z.object({ inventoryItemId: z.number().int().positive() });
/**
 * What the vendor says a Name your gear card is (Decisions 105 and 112): the
 * catalog model it is, not in the catalog, which makes it from the line's
 * words for admin, or an item of their own, which the line links.
 */
export const NamingChoiceSchema = z.union([NamedModelSchema, NotInCatalogSchema, YourItemSchema]);
export type NamingChoice = z.infer<typeof NamingChoiceSchema>;

/**
 * A line of the batch the vendor names (Name your gear, Decisions 104, 105,
 * 109 and 112), a card each however many of the batch's receipts bought it:
 * gear no maker's number names (its model unknown), gear whose maker's
 * number names a model the vendor owns none of (`model`), or an item of
 * theirs with no model a line links (`inventoryItemId`). The first two offer
 * the vendor's own items the line's words find (`yours`); the first and the
 * last the catalog's guess and its search. `startsOn` is the answer the card
 * opens on, which the review judges by until the vendor answers: their item
 * when the words find one, else the guess, else the line's model; null when
 * none, and the vendor must answer.
 */
const BatchNamingItemSchema = z.object({
  /** Names the card for its answer: a new item's key, as New gear's, or the vendor's item's. */
  key: z.string(),
  title: z.string(),
  /** The vendor's item the lines link, which takes the model named and its name (Decision 108); null for a line New gear would make. */
  inventoryItemId: z.number().int().positive().nullable(),
  /** The maker's part number the newest receipt printed. */
  partNumber: z.string().nullable(),
  receipts: z.array(BatchReceiptUnitsSchema).min(1),
  units: z.number().int().positive(),
  /** The catalog model the line's maker's number names, which "New" makes; null when none names one. */
  model: BatchModelChoiceSchema.nullable(),
  /**
   * The vendor's items the line's words find, one per name, the oldest, each
   * with no model or the line's (Decision 112); first, of a guess the vendor
   * owns gear of, that item; and the item they answered, as the batch was
   * read with it ("I already have this", Decision 99).
   */
  yours: z.array(BatchYourItemSchema),
  /**
   * The catalog model the card guesses, of none of the vendor's gear: the one
   * the vendor named for this store's line before, then another vendor's,
   * then the catalog search's top (Decision 106); null when none finds one,
   * or the line's model is known.
   */
  guess: BatchModelChoiceSchema.nullable(),
  /**
   * The model the vendor named for it, as the batch was read with their
   * answers (BatchQuerySchema's `named`); null where they named none, answered
   * otherwise, or named one the catalog no longer has.
   */
  named: BatchModelChoiceSchema.nullable(),
  startsOn: NamingChoiceSchema.nullable(),
});

/**
 * GET vendor/receipts/batch: what each of the screens shows, in order
 * (Decision 86); an empty one is skipped. Check receipts asks of the whole
 * batch, a card per receipt. Name your gear, Your stock and New gear ask of
 * the batch as judged by the vendor's answers there: receipts set aside to
 * wait for a fix or skipped are left out, and what they bought with them;
 * those showing no payment the vendor uses are in, and so is each file kept
 * in its receipt's place, with what they bought; Your stock and New gear
 * are judged with the models named (Decision 109). The page works nothing
 * out of this; it shows it and sends the answers.
 */
export const BatchSchema = z.object({
  /** Check receipts' cards, newest order first (Decision 87). */
  checks: z.array(BatchCheckSchema),
  /** Name your gear's cards: the batch's gear no maker's number or earlier answer names, as read before the models named here. */
  naming: z.array(BatchNamingItemSchema),
  owned: z.array(BatchOwnedItemSchema),
  newItems: z.array(BatchNewItemSchema),
  /** The receipts in the batch, those showing no payment too, and each receipt with copies: what the Tax Compliance page counts as waiting to review. */
  waitingCount: z.number().int().nonnegative(),
  /** Settled receipts not yet signed: what Sign opens with. */
  readyCount: z.number().int().nonnegative(),
  /** Of the receipts waiting, those the listings need (BATCH_SCOPE.LISTINGS): what Decision 96's review takes. */
  listingsCount: z.number().int().nonnegative(),
});
export type Batch = z.infer<typeof BatchSchema>;
export const BatchEnvelopeSchema = envelope(BatchSchema);
export const unwrapBatch = (response: z.infer<typeof BatchEnvelopeSchema>) => response.data;

/** Each value once: a receipt answered once, an item asked once. */
const eachOnce = (values: Array<number | string>) => new Set(values).size === values.length;

const IDS_SEPARATOR = ',';

/** Receipts as the batch's address carries them (the `wait`, `use` and `keep` query parameters). */
export const receiptIdsToQuery = (receiptIds: number[]): string => receiptIds.join(IDS_SEPARATOR);

/** A list of receipts in the batch's address, none when it names none, each once. */
const receiptIdsParam = z
  .string()
  .default('')
  .transform((text, ctx) => {
    if (text === '') return [];
    const ids = text.split(IDS_SEPARATOR).map(Number);
    if (ids.some((id) => !Number.isInteger(id) || id <= 0)) {
      ctx.addIssue({ code: 'custom', message: 'That is not a receipt of this batch.' });
      return z.NEVER;
    }
    return ids;
  })
  .pipe(z.array(z.number().int().positive()).refine(eachOnce, 'Each receipt is named once.'));

/**
 * Which receipts a batch read or an Add to inventory takes, when not the
 * whole batch (Decision 96; Tyler, 2026-10-19: "a review flow just for the
 * items that need a receipt"): just the receipts the listings need, each a
 * line of which links to gear needing a receipt, every line of each asked.
 */
export const BATCH_SCOPE = { LISTINGS: 'listings' } as const;
const BatchScopeSchema = z.enum(BATCH_SCOPE);

/** A Name your gear card's answer: its key, and what the vendor says it is. */
const NamingAnswerSchema = z.union([
  NamedModelSchema.extend({ key: z.string() }),
  NotInCatalogSchema.extend({ key: z.string() }),
  YourItemSchema.extend({ key: z.string() }),
]);
export type NamingAnswer = z.infer<typeof NamingAnswerSchema>;

/**
 * Name your gear answered as each line prints it, for what adds receipts as
 * read (a seed, a store's own tools): the model its maker's number names,
 * which New gear makes, else not in the catalog, which makes new gear from
 * the line's words and keeps an item of the vendor's as it is.
 */
export const namingAsPrinted = (naming: Batch['naming']): NamingAnswer[] =>
  naming.map((card) => (card.model === null ? { key: card.key, notInCatalog: true } : { key: card.key, productModelId: card.model.productModelId }));
const NamingAnswersSchema = z.array(NamingAnswerSchema).refine((answers) => eachOnce(answers.map((answer) => answer.key)), 'Each item is named once.');

/** A list as the batch's address carries it: JSON, as a card's key holds any character. */
const listToQuery = (list: unknown[]): string => JSON.stringify(list);

/** Name your gear's answers as the batch's address carries them (the `named` query parameter, Decision 109). */
export const namedToQuery = (named: NamingAnswer[]): string => listToQuery(named);

/** A list in the batch's address (listToQuery), none when it names none; `message` says what a list that is not one is not. */
const listParam = <T extends z.ZodType>(list: T, message: string) =>
  z
    .string()
    .default('[]')
    .transform((text, ctx): unknown => {
      try {
        return JSON.parse(text);
      } catch (err) {
        ctx.addIssue({ code: 'custom', message, params: { parseError: err instanceof Error ? err.message : String(err) } });
        return z.NEVER;
      }
    })
    .pipe(list);

/**
 * GET vendor/receipts/batch's query: the receipts left out of this visit,
 * set aside to wait for a fix or skipped for the next review (Decision 79);
 * the receipts showing no payment the vendor uses (Decision 74); the file
 * kept of each receipt with copies, in its receipt's place (Decision 87);
 * and Name your gear's answers, a model, not in the catalog, or an item of
 * the vendor's (Decisions 109 and 112); each none when it names none. With
 * a scope, just those receipts of the batch (BATCH_SCOPE); with none, the
 * whole batch.
 */
export const BatchQuerySchema = z.object({
  wait: receiptIdsParam,
  use: receiptIdsParam,
  keep: receiptIdsParam,
  named: listParam(NamingAnswersSchema, 'That is not a name given for this batch.'),
  scope: BatchScopeSchema.optional(),
});
export type BatchQuery = z.infer<typeof BatchQuerySchema>;

/** What the vendor says of a receipt read with trouble (Decisions 17 and 32). */
export const TROUBLE_CHOICE = {
  /** Its gear and proof of purchase were read correctly: keep it in the batch, written as read. */
  USE_AS_READ: 'USE_AS_READ',
  /**
   * There were issues with how it was read: leave it out of every batch
   * until admin's Read again gives it a new read, and tell support, who
   * contact the vendor.
   */
  WAIT_FOR_FIX: 'WAIT_FOR_FIX',
} as const;

/**
 * What the vendor does with a receipt showing no payment, on Read trouble
 * (Decision 74): only what GearGhost can do with a receipt, never what kind
 * of payment it was (Tyler, 2026-10-16). It needs no answer: one left
 * unanswered waits (Decision 33).
 */
export const UNPAID_CHOICE = {
  /** It goes through like any receipt: its gear on the next screens, settled and signed with the rest; the vendor decides (Decision 73). */
  USE: 'USE',
  /** It is deleted: its file and its read (Decision 75). */
  REMOVE: 'REMOVE',
} as const;

/**
 * A new item's answer on New gear: Don't add (Decision 23), or add it, with
 * Track Quantity, and How many do you own while it is on (Decision 20). The
 * vendor's own gear a line is, is Name your gear's answer (Decision 112).
 */
const NewItemAnswerSchema = z.union([
  z.object({ key: z.string(), add: z.literal(false) }),
  z.object({ key: z.string(), add: z.literal(true), trackQuantity: z.literal(false) }),
  z.object({ key: z.string(), add: z.literal(true), trackQuantity: z.literal(true), ownNow: z.number().int().positive() }),
]);
export type NewItemAnswer = z.infer<typeof NewItemAnswerSchema>;

/**
 * POST vendor/receipts/batch, Add to inventory: the answer to every question
 * the batch's screens asked, written at once, and to those receipts showing
 * no payment and receipts with copies the vendor answered, which need none
 * (Decisions 74 and 84). Trouble and sales tax answers name the file asked
 * about, the file kept for a receipt with copies. The server judges the
 * batch again, with the receipts used and the files kept, and refuses
 * answers that are not its questions (a stale page).
 */
export const AddToInventoryRequestSchema = z.object({
  trouble: z
    .array(z.object({ receiptId: z.number().int().positive(), choice: z.enum(TROUBLE_CHOICE) }))
    .refine((answers) => eachOnce(answers.map((answer) => answer.receiptId)), 'Each receipt is answered once.'),
  salesTax: z
    .array(z.object({ receiptId: z.number().int().positive(), paidCaliforniaSalesTax: z.boolean() }))
    .refine((answers) => eachOnce(answers.map((answer) => answer.receiptId)), 'Each receipt is answered once.'),
  /** How many you own of each item Your stock asks, gear named on Name your gear among them (Decision 112): once an item. */
  owned: z
    .array(z.object({ inventoryItemId: z.number().int().positive(), ownNow: z.number().int().nonnegative() }))
    .refine((answers) => eachOnce(answers.map((answer) => answer.inventoryItemId)), 'Each item is answered once.'),
  newItems: z.array(NewItemAnswerSchema).refine((answers) => eachOnce(answers.map((answer) => answer.key)), 'Each new item is answered once.'),
  unpaid: z
    .array(z.object({ receiptId: z.number().int().positive(), choice: z.enum(UNPAID_CHOICE) }))
    .refine((answers) => eachOnce(answers.map((answer) => answer.receiptId)), 'Each receipt is answered once.'),
  /**
   * The file kept of each receipt with copies the vendor answered (Decision
   * 84), named by the receipt's id (its card's); the rest of its files are
   * deleted. A card unanswered needs none, and waits.
   */
  copies: z
    .array(z.object({ receiptId: z.number().int().positive(), keep: z.number().int().positive() }))
    .refine((answers) => eachOnce(answers.map((answer) => answer.receiptId)), 'Each receipt is answered once.'),
  /**
   * The receipts Skip for now leaves for the next review, whatever Check
   * receipts asked of them (Decision 79; Tyler, 2026-10-18: "i want to be
   * able to skip the first screen entirely"): left out of this Add to
   * inventory, never set aside, answered nothing.
   */
  skipped: z.array(z.number().int().positive()).refine(eachOnce, 'Each receipt is skipped once.'),
  /** Name your gear's answers, one for each card the vendor answered (Decision 109); a card left as it opened takes its `startsOn`. */
  named: NamingAnswersSchema.default([]),
  /** The review the answers came from, when not the whole batch's (BATCH_SCOPE): only its receipts are written, the rest left waiting. */
  scope: BatchScopeSchema.optional(),
});
export type AddToInventoryRequest = z.infer<typeof AddToInventoryRequestSchema>;

/** What Add to inventory and the batch's settle answer: how many receipts are ready to sign now. */
export const BatchSettledSchema = z.object({
  readyCount: z.number().int().nonnegative(),
  /** The settle's answer: true while a receipt of the batch still needs the screens. */
  needsVendor: z.boolean(),
  /** How many still need the vendor once settled: what Receipts to review shows, and the upload notice says (Decision 54). */
  waitingCount: z.number().int().nonnegative(),
});
export type BatchSettled = z.infer<typeof BatchSettledSchema>;
export const BatchSettledEnvelopeSchema = envelope(BatchSettledSchema);
export const unwrapBatchSettled = (response: z.infer<typeof BatchSettledEnvelopeSchema>) => response.data;

/**
 * An item's count and the units its signed, qualifying receipts show the tax
 * paid on: the one rule the Taxes page reads (TaxPaidItemSchema) and a
 * receipt's review shows before and after signing.
 */
export const TaxPaidStandingSchema = z.object({
  trackQuantity: z.boolean(),
  quantity: z.number().int(),
  /** Unmodified units on qualifying receipts, never more than the item has when its count is kept. */
  taxPaidUnits: z.number().int().nonnegative(),
  /** The units still needing a receipt; null for an untracked item, whose count is not kept. */
  unitsNeedingReceipts: z.number().int().nonnegative().nullable(),
  /** Every unit is tax paid; an untracked item is once a qualifying receipt names it. */
  taxPaid: z.boolean(),
});
export type TaxPaidStanding = z.infer<typeof TaxPaidStandingSchema>;

// ─── The sign screen (#1272) ────────────────────────────────────────────

/** One item a settled receipt points at, as the sign screen lists it. */
const ReadyGearSchema = z.object({
  inventoryItemId: z.number().int(),
  title: z.string(),
  units: z.number().int().positive(),
  /** True for an item the receipt's review made. */
  made: z.boolean(),
  modified: z.boolean(),
});

/**
 * GET vendor/receipts/ready: every settled receipt not yet signed that
 * covers a unit, with the gear it covers and the answers it will sign
 * (Decision 10: the sign screen opens at any moment, with whatever is ready
 * then). A receipt covering none is settled but has nothing to sign
 * (Decision 25).
 */
export const ReadyReceiptsSchema = z.object({
  receipts: z.array(
    z.object({
      ...receiptHeadingFields,
      /** The state the receipt shows it was bought in; null when it shows none (Decision 19). */
      purchaseState: UsStateCodeSchema.nullable(),
      paidCaliforniaSalesTax: z.boolean(),
      gear: z.array(ReadyGearSchema),
    }),
  ),
  count: z.number().int().nonnegative(),
  /** The name the signature will hold: the signed-in person's account name, never typed (taxes.md Decision 28). */
  signedAs: z.string(),
});
export type ReadyReceipts = z.infer<typeof ReadyReceiptsSchema>;
export const ReadyReceiptsEnvelopeSchema = envelope(ReadyReceiptsSchema);
export const unwrapReadyReceipts = (response: z.infer<typeof ReadyReceiptsEnvelopeSchema>) => response.data;

/**
 * POST vendor/receipts/signature: one signature for every settled receipt
 * named, each on its own signature row with the statement (taxes.md Decision
 * 28). It approves what each points at and changes no gear (Tyler,
 * 2026-10-08: "the sign is just approve what the receipts point at").
 */
export const SignReceiptsRequestSchema = z.object({
  receiptIds: z
    .array(z.number().int().positive())
    .min(1, 'Pick the receipts to sign.')
    .refine((ids) => new Set(ids).size === ids.length, 'Each receipt is signed once.'),
});
export type SignReceiptsRequest = z.infer<typeof SignReceiptsRequestSchema>;

/** What signing did: the receipts it signed, in the order it signed them. */
export const SignedReceiptsSchema = z.object({ signed: z.array(z.number().int()) });
export type SignedReceipts = z.infer<typeof SignedReceiptsSchema>;
export const SignedReceiptsEnvelopeSchema = envelope(SignedReceiptsSchema);
export const unwrapSignedReceipts = (response: z.infer<typeof SignedReceiptsEnvelopeSchema>) => response.data;

/** A signature's item, with the item's title so the page names it; new gear is an item by then. */
export const ReceiptSignatureItemSchema = z.object({
  inventoryItemId: z.number().int(),
  title: z.string(),
  ...receiptRowFields,
});

export const ReceiptSignatureSchema = z.object({
  id: z.number().int(),
  paidCaliforniaSalesTax: z.boolean(),
  /** The state the receipt shows it was bought in; null when it showed none, since it is never asked (Decision 19). */
  purchaseState: UsStateCodeSchema.nullable(),
  resaleCertificateUsed: z.boolean(),
  /** Whether this receipt counts toward its items' tax paid: California sales tax paid and no resale certificate. */
  qualifies: z.boolean(),
  items: z.array(ReceiptSignatureItemSchema),
  /** The words signed, as they read when the vendor signed. */
  statement: z.string(),
  signedName: z.string(),
  signedAt: IsoDateTimeString,
});

/** A vendor's receipt on the Taxes page: the receipt and its latest signature, or null before it is signed. */
/**
 * Why a line of a receipt covering none of the vendor's units is not
 * attached (Decision 25): the units the vendor owns of its item are all on
 * newer receipts (Decision 27), they own none now, its item was deleted, or
 * the line was left out at Add to inventory (Don't add, Decision 23).
 */
export const RECEIPT_NOT_ATTACHED = {
  ON_NEWER_RECEIPTS: 'ON_NEWER_RECEIPTS',
  NONE_OWNED: 'NONE_OWNED',
  ITEM_DELETED: 'ITEM_DELETED',
  NOT_ADDED: 'NOT_ADDED',
} as const;

const NotAttachedWhySchema = z.discriminatedUnion('kind', [
  /** How many the vendor owns of the item, every one on a newer receipt. */
  z.object({ kind: z.literal(RECEIPT_NOT_ATTACHED.ON_NEWER_RECEIPTS), owned: z.number().int().positive() }),
  z.object({ kind: z.literal(RECEIPT_NOT_ATTACHED.NONE_OWNED) }),
  z.object({ kind: z.literal(RECEIPT_NOT_ATTACHED.ITEM_DELETED) }),
  z.object({ kind: z.literal(RECEIPT_NOT_ATTACHED.NOT_ADDED) }),
]);
export type NotAttachedWhy = z.infer<typeof NotAttachedWhySchema>;

/** A line of a receipt covering none, as the receipt printed it, and why it is not attached. */
/** A line of a receipt not attached, as printed, with the units it bought (the receipt's page, Tyler, 2026-10-10) and why. */
const NotAttachedLineSchema = z.object({ description: z.string(), units: z.number().int().positive(), why: NotAttachedWhySchema });

/**
 * Why a line is not attached, in words, for the Tax Compliance list's card
 * (Tyler, 2026-10-09: "lets follow whatever wording we use on the other
 * screens"): one wording for every app, as mathProblemText is.
 */
export const notAttachedWhyText = (why: NotAttachedWhy): string => {
  if (why.kind === RECEIPT_NOT_ATTACHED.ON_NEWER_RECEIPTS) {
    return why.owned === 1 ? 'Your 1 is on a newer receipt' : `Your ${why.owned} are on newer receipts`;
  }
  if (why.kind === RECEIPT_NOT_ATTACHED.NONE_OWNED) return 'You own none of these now';
  if (why.kind === RECEIPT_NOT_ATTACHED.ITEM_DELETED) return 'Its item was deleted';
  return 'Not added';
};

export const VendorReceiptSchema = ReceiptSchema.extend({
  signature: ReceiptSignatureSchema.nullable(),
  /**
   * A receipt past the review that covers none of the vendor's units,
   * worked out when it is read (Decisions 25 and 27): each line and why it
   * is not attached. Null for a receipt that covers a unit, or that waits
   * for its review.
   */
  notAttached: z.array(NotAttachedLineSchema).nullable(),
  /**
   * The gear a receipt past the review is matched to: each of the vendor's
   * items it covers units of now, with how many, worked out when it is read
   * (Decision 27). Empty for one covering none, whose lines say why
   * (`notAttached`); null for a receipt that waits for its review. What the
   * Tax Compliance list shows of a receipt: its store, its date, whether it
   * is signed, and this (Tyler, 2026-10-10: "This is not a book keeping
   * product").
   */
  matchedGear: z
    .array(z.object({ inventoryItemId: z.number().int().positive(), title: z.string(), units: z.number().int().positive() }))
    .nullable(),
  /**
   * The vendor said it was read wrong (There were issues with how it was
   * read, Decision 32), and it waits for admin's Read again. The Tax
   * Compliance list keeps it, saying GearGhost is working on it (Decision
   * 48); it is in no batch and signs nothing until then.
   */
  waitingForFix: z.boolean(),
});
export type VendorReceipt = z.infer<typeof VendorReceiptSchema>;

export const VendorReceiptEnvelopeSchema = envelope(VendorReceiptSchema);
export const unwrapVendorReceipt = (response: z.infer<typeof VendorReceiptEnvelopeSchema>) => response.data;

// ─── Listing the vendor's receipts (the Taxes page) ─────────────────────

/** The list's sorts; '' is newest first. */
export const RECEIPT_LIST_SORT_VALUES = ['', 'oldest'] as const;
export type ReceiptListSort = (typeof RECEIPT_LIST_SORT_VALUES)[number];

/**
 * The list's status filter: '' is every receipt; unsigned waits for the
 * vendor's signature; signed; reviewed is every receipt past the review,
 * signed or settled, which the Tax Compliance page lists (Decision 26);
 * missed is a receipt whose read has a miss (a line or amount it could not
 * read, or no tax or payment shown).
 */
export const RECEIPT_LIST_STATUS_VALUES = ['', 'unsigned', 'signed', 'reviewed', 'missed'] as const;
export type ReceiptListStatus = (typeof RECEIPT_LIST_STATUS_VALUES)[number];

/** The store filter's value for a receipt from a store with no parser yet. */
export const RECEIPT_OTHER_STORE = 'other';

/** The list's store filter: '' is every store, then each store with a parser, then the rest. */
export const RECEIPT_LIST_STORE_VALUES = ['', ...RECEIPT_STORE_KEYS, RECEIPT_OTHER_STORE] as const;
export type ReceiptListStore = (typeof RECEIPT_LIST_STORE_VALUES)[number];

/** How many receipts a page holds, unless the request asks for another size. */
export const RECEIPT_LIST_PAGE_SIZE = 20;
export const RECEIPT_LIST_MAX_PAGE_SIZE = 50;

/**
 * GET vendor/receipts: a page of the vendor's receipts. Every parameter is
 * optional: page 1, RECEIPT_LIST_PAGE_SIZE, no search, every status and
 * store, newest first. The search matches a file name, an order number or
 * a store's name.
 */
export const ListReceiptsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(RECEIPT_LIST_MAX_PAGE_SIZE).default(RECEIPT_LIST_PAGE_SIZE),
  search: z.string().trim().max(100).default(''),
  status: z.enum(RECEIPT_LIST_STATUS_VALUES).default(''),
  store: z.enum(RECEIPT_LIST_STORE_VALUES).default(''),
  sort: z.enum(RECEIPT_LIST_SORT_VALUES).default(''),
});
/** What a caller sends: any of the parameters, as the page holds them (the query string carries them as text). */
export type ListReceiptsQuery = Partial<z.infer<typeof ListReceiptsQuerySchema>>;

/** A page of the vendor's receipts: its rows, how many receipts the filters keep, and the page size the server used. */
export const VendorReceiptPageSchema = z.object({
  list: z.array(VendorReceiptSchema),
  count: z.number().int().nonnegative(),
  pageSize: z.number().int().positive(),
});
export type VendorReceiptPage = z.infer<typeof VendorReceiptPageSchema>;
export const VendorReceiptListEnvelopeSchema = envelope(z.array(VendorReceiptSchema));

export const unwrapVendorReceiptPage = (response: z.infer<typeof VendorReceiptListEnvelopeSchema>): VendorReceiptPage =>
  pageOf(RECEIPT_ROUTES.receipts, response);

/**
 * One inventory item and the units its signed, qualifying receipts show the
 * tax paid on. "Tax paid", never "coverage", which is Damage + Theft's
 * (Decision 26).
 */
/**
 * Why an item needs a tax record, or does not (docs/plans/taxes.md, Decision
 * 111): an item never needs one on its own, but because of the listings it
 * is on.
 */
export const TAX_RECORD_REASON = {
  /** A listing's main item: always. */
  MAIN_ITEM: 'MAIN_ITEM',
  /** Included gear one unit of which costs at least the platform's share of a listing's whole replacement cost to replace. */
  INCLUDED_GEAR: 'INCLUDED_GEAR',
  /** Included gear under the share on every listing it is on: no record needed. */
  UNDER_SHARE: 'UNDER_SHARE',
  /** On no listing yet, a checklist item among them: no record needed. */
  NOT_LISTED: 'NOT_LISTED',
} as const;

export const TaxPaidItemSchema = TaxPaidStandingSchema.extend({
  inventoryItemId: z.number().int(),
  title: z.string(),
  /** Whether the item's listings make it need a tax record, and why (Decision 111). */
  record: z.object({ needed: z.boolean(), reason: z.enum(TAX_RECORD_REASON) }),
});
export type TaxPaidItem = z.infer<typeof TaxPaidItemSchema>;

/**
 * The vendor's tax paid: every item, and how many of their listings (not
 * deleted, drafts included) include gear no receipt shows the tax paid on
 * yet, the banner's count on the store's Listings and Inventory pages
 * (Decisions 15 and 16: a listing on its old Paid Tax counts too).
 */
export const TaxPaidSchema = z.object({
  items: z.array(TaxPaidItemSchema),
  /** How many items a signed receipt does not show the tax paid on in full: the Taxes page's count, and the Inventory page's needed filter. */
  itemsNeedingReceipts: z.number().int().nonnegative(),
  listingsNeedingReceipts: z.number().int().nonnegative(),
  /** How many of the vendor's receipts are signed: Tax Compliance's Signed Receipts tile (Tyler, 2026-10-18). */
  signedReceipts: z.number().int().nonnegative(),
});
export type TaxPaid = z.infer<typeof TaxPaidSchema>;
export const TaxPaidEnvelopeSchema = envelope(TaxPaidSchema);
export const unwrapTaxPaid = (response: z.infer<typeof TaxPaidEnvelopeSchema>) => response.data;

/** What admin sees: the vendor's receipt, plus the file's type, the store, and who uploaded it. */
export const AdminReceiptSchema = ReceiptSchema.extend({
  /** The type read from the file's own bytes. */
  mimeType: z.string(),
  vendor: z.object({ id: z.string(), storeName: z.string() }),
  uploadedBy: z.object({ id: z.string(), firstName: z.string(), lastName: z.string() }),
  /** The proof of payment the vendor attached because the receipt shows none (Decision 37); null when none was. */
  paymentProof: PaymentProofSchema.nullable(),
});
export type AdminReceipt = z.infer<typeof AdminReceiptSchema>;

export const AdminReceiptEnvelopeSchema = envelope(AdminReceiptSchema);
export const unwrapAdminReceipt = (response: z.infer<typeof AdminReceiptEnvelopeSchema>) => response.data;

/** GET admin/receipts/:id/file-url, and an upload's misses-url: the shared short-lived link. */
export const AdminReceiptFileUrlEnvelopeSchema = ShortLivedLinkEnvelopeSchema;

// ─── Gear waiting for a catalog model (Decisions 32, 33 and 35 to 38) ────

/**
 * Admin's list of gear a signed receipt made with no catalog model, and the
 * tool that makes each one's model. SUPER_ADMIN only, as receipts are.
 */
export const ADMIN_CATALOG_MODEL_ROUTES = {
  /** GET: the open requests, oldest first, a page at a time (ListCatalogModelRequestsQuerySchema). */
  requests: 'admin/catalog-models/requests',
  /** POST: make the request's model, or find it, and give it the waiting gear (MakeCatalogModelRequestSchema). */
  model: (requestId: number | string) => `admin/catalog-models/requests/${requestId}/model`,
  /** GET: the catalog's brands whose name holds the search (SearchCatalogBrandsQuerySchema), for the tool's brand. */
  brands: 'admin/catalog-models/brands',
  /** GET: the catalog's live categories, each with its live subcategories, for the tool's category. */
  categories: 'admin/catalog-models/categories',
} as const;

export const CATALOG_MODEL_REQUEST_PAGE_SIZE = 25;

/** The longest brand or model name the tool takes, as the inventory form's names run. */
export const CATALOG_MODEL_NAME_MAX_LENGTH = 200;

/** GET admin/catalog-models/requests: a page of the open requests; page 1 unless asked. */
export const ListCatalogModelRequestsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
});

/**
 * What is known of a product waiting for its model, a field null where it was
 * left empty: UPCitemdb's first product for a part number (Decision 33), or
 * an import's own lookup (docs/plans/vendor-api.md, Decision 52), which alone
 * carries the page it came from, and its price before tax when found.
 */
export const CatalogModelSuggestionSchema = z.object({
  title: z.string().nullable(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  category: z.string().nullable(),
  price: NonNegativeMoneyCents.optional(),
  link: z.url({ protocol: /^https?$/ }).optional(),
});
export type CatalogModelSuggestion = z.infer<typeof CatalogModelSuggestionSchema>;

/**
 * What the tool's form opens with, the server's reading of the suggestion:
 * the catalog brand its brand names (any case), else a new brand of that
 * name; and the model name, the suggestion's title less that brand at its
 * front, the way the catalog names models. Admin confirms or corrects both;
 * null where the suggestion has nothing to give.
 */
export const CatalogModelPrefillSchema = z.object({
  brand: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('existing'), productBrandId: z.number().int(), name: z.string() }),
      z.object({ kind: z.literal('new'), name: z.string() }),
    ])
    .nullable(),
  modelName: z.string().nullable(),
});
export type CatalogModelPrefill = z.infer<typeof CatalogModelPrefillSchema>;

/** One item waiting for a catalog model, as admin's list shows it. */
export const CatalogModelRequestSchema = z.object({
  id: z.number().int(),
  item: z.object({ id: z.number().int(), title: z.string() }),
  vendor: z.object({ id: z.string(), storeName: z.string() }),
  /** The receipt that made the item; null once the vendor deleted it, as the gear still needs its model (Decision 81). */
  receiptId: z.number().int().nullable(),
  /** The vendor API import that made the item; null for a receipt's. */
  importId: z.number().int().nullable(),
  /** The line's own words, as the receipt printed them, or the item's name as an import sent it. */
  lineDescription: z.string(),
  /** The maker's part number, when the store printed one or the import sent it. */
  partNumber: z.string().nullable(),
  /** Null until UPCitemdb found a product for the part number, unless an import's lookup came with the item. */
  suggestion: CatalogModelSuggestionSchema.nullable(),
  prefill: CatalogModelPrefillSchema,
  /** When UPCitemdb was searched, found or not; null while the search waits. */
  lookedUpAt: IsoDateTimeString.nullable(),
  createdAt: IsoDateTimeString,
});
export type CatalogModelRequest = z.infer<typeof CatalogModelRequestSchema>;

export const CatalogModelRequestPageSchema = z.object({
  list: z.array(CatalogModelRequestSchema),
  count: z.number().int().nonnegative(),
  pageSize: z.number().int().positive(),
});
export type CatalogModelRequestPage = z.infer<typeof CatalogModelRequestPageSchema>;
export const CatalogModelRequestListEnvelopeSchema = envelope(z.array(CatalogModelRequestSchema));
export const unwrapCatalogModelRequestPage = (
  response: z.infer<typeof CatalogModelRequestListEnvelopeSchema>,
): CatalogModelRequestPage => pageOf(ADMIN_CATALOG_MODEL_ROUTES.requests, response);

const catalogName = (field: string) =>
  z
    .string()
    .trim()
    .min(1, `Give the ${field}.`)
    .max(CATALOG_MODEL_NAME_MAX_LENGTH, `The ${field} can be ${CATALOG_MODEL_NAME_MAX_LENGTH} characters at most.`);

/**
 * POST admin/catalog-models/requests/:id/model: the model to make, or find,
 * for the request's gear. The brand is one the catalog has, or a new one.
 * The model's title is the brand and the model name together, the way the
 * catalog's titles read, and the waiting gear takes it exactly (Decision
 * 37). The part number, the line's unless admin corrects it, goes beside the
 * model (Decision 36), so later receipts with it match on their own.
 */
export const MakeCatalogModelRequestSchema = z.object({
  brand: z.discriminatedUnion(
    'kind',
    [
      z.object({ kind: z.literal('existing'), productBrandId: z.number().int().positive() }),
      z.object({ kind: z.literal('new'), name: catalogName('brand') }),
    ],
    { error: 'Pick a brand, or make it new.' },
  ),
  modelName: catalogName('model name'),
  productCategoryId: z.number({ error: 'Pick a category.' }).int().positive(),
  productSubCategoryId: z.number().int().positive().nullable(),
  partNumber: z.string().trim().min(1).max(CATALOG_MODEL_NAME_MAX_LENGTH).nullable(),
  /** Its price new, before tax, as the catalog keeps prices; a model with none has nothing to offer the next vendor. */
  price: NonNegativeMoneyCents.nullable().optional(),
});
export type MakeCatalogModelRequest = z.infer<typeof MakeCatalogModelRequestSchema>;

/** What making the model did: the model, and each request its gear resolved. */
export const MadeCatalogModelSchema = z.object({
  productModel: z.object({ id: z.number().int(), title: z.string() }),
  resolvedRequestIds: z.array(z.number().int()),
});
export type MadeCatalogModel = z.infer<typeof MadeCatalogModelSchema>;
export const MadeCatalogModelEnvelopeSchema = envelope(MadeCatalogModelSchema);
export const unwrapMadeCatalogModel = (response: z.infer<typeof MadeCatalogModelEnvelopeSchema>) => response.data;

/** GET admin/catalog-models/brands: the search the brand field sends. */
export const SearchCatalogBrandsQuerySchema = z.object({
  search: z.string().trim().min(1).max(100),
});

export const CatalogBrandSchema = z.object({ id: z.number().int(), name: z.string() });
export type CatalogBrand = z.infer<typeof CatalogBrandSchema>;
export const CatalogBrandListEnvelopeSchema = envelope(z.array(CatalogBrandSchema));
export const unwrapCatalogBrands = (response: z.infer<typeof CatalogBrandListEnvelopeSchema>) => response.data;

/** A live category and its live subcategories, in the catalog's display order. */
export const CatalogCategorySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  subCategories: z.array(z.object({ id: z.number().int(), name: z.string() })),
});
export type CatalogCategory = z.infer<typeof CatalogCategorySchema>;
export const CatalogCategoryListEnvelopeSchema = envelope(z.array(CatalogCategorySchema));
export const unwrapCatalogCategories = (response: z.infer<typeof CatalogCategoryListEnvelopeSchema>) => response.data;
